import { SelectChangeEvent } from '@mui/material';
import { GENERATE_REPORTS_VALUES, REPORT_TYPES, WEEKS } from '../constants';
import { useNotify } from 'react-admin';

export const handleStartDateChange = (
  e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  setStartDate: (value: string) => void,
  endDate: string,
  notify: (message: string, options?: { type?: 'info' | 'success' | 'warning' | 'error'; undoable?: boolean }) => void,
  setStartDateInvalid: (flag: boolean) => void,
  setEndDateInvalid: (flag: boolean) => void
) => {
  const newStartDate = e.target.value;
  setStartDate(newStartDate);

  if (isDateInFuture(newStartDate)) {
    notify('notifications.START_DATE_IN_FUTURE', { type: 'error' });
    setStartDateInvalid(true);
    return;
  }

  if (endDate && newStartDate >= endDate) {
    notify('notifications.START_DATE_AFTER_END', { type: 'error' });
    setStartDateInvalid(true);
    setEndDateInvalid(true);
    return;
  }
  setStartDateInvalid(false);
  setEndDateInvalid(false);
};

export const handleEndDateChange = (
  e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  setEndDate: (value: string) => void,
  startDate: string,
  latestClaimDate: string | null,
  notify: (message: string, options?: { type?: 'info' | 'success' | 'warning' | 'error'; undoable?: boolean }) => void,
  setEndDateInvalid: (flag: boolean) => void,
  setStartDateInvalid: (flag: boolean) => void
) => {
  const newEndDate = e.target.value;

  setEndDate(newEndDate);

  if (isDateInFuture(newEndDate)) {
    notify('notifications.END_DATE_IN_FUTURE', { type: 'error' });
    setEndDateInvalid(true);
    return;
  }

  if (startDate && startDate >= newEndDate) {
    notify('notifications.END_DATE_BEFORE_START', { type: 'error' });
    setEndDateInvalid(true);
    setStartDateInvalid(true);
    return;
  }

  if (latestClaimDate) {
    const selectedEnd = new Date(newEndDate);
    const latest = new Date(latestClaimDate);

    if (selectedEnd > latest) {
      notify('notifications.GENERATE_REPORT.LATEST_CLAIM_DATE_ERROR', {
        type: 'warning',
      });
    }
  }
  setEndDateInvalid(false);
  setStartDateInvalid(false);
};

export const handleReportTypeChange = async (
  e: SelectChangeEvent<string>,
  setReportType: (value: string) => void,
  setAvailableWeeks: (weeks: string[]) => void,
  setShowWeekDropdown: (value: boolean) => void,
  setDisabledFields: (value: boolean) => void,
  setSelectedWeek: (value: string) => void,
  setSelectedMonth: (value: string) => void,
  setSelectedYear: (value: number) => void,
  setStartDate: (value: string) => void,
  setEndDate: (value: string) => void,
  setBillingDate: (value: string) => void,
  setStartDateInvalid: (value: boolean) => void,
  setEndDateInvalid: (value: boolean) => void,
  setClients: (value: any[]) => void,
  setSelectedTpas: (value: any[]) => void,
  setFilterType: (value: string) => void,
  setIsMedSubReport: (value: boolean) => void,
  reportType: string
) => {
  const selectedType = e.target.value as string;

  const usesSpecialFilters = (type: string) =>
    type === REPORT_TYPES.MEMBER_SUMMARY ||
    type === REPORT_TYPES.PA_STATUS_REPORT ||
    type === REPORT_TYPES.INVOICE_DETAIL;

  const isSpecial = usesSpecialFilters(selectedType);
  const wasSpecial = usesSpecialFilters(reportType);

  setSelectedWeek?.('');
  setSelectedMonth('');
  setStartDate('');
  setEndDate('');
  setBillingDate('');
  setReportType(selectedType);
  setShowWeekDropdown(false);
  setDisabledFields(false);
  setStartDateInvalid(false);
  setEndDateInvalid(false);

  setSelectedWeek?.('');
  setSelectedMonth?.('');
  setSelectedYear?.(new Date().getFullYear());

  if (selectedType !== reportType && (isSpecial || wasSpecial)) {
    setClients([]);
    setSelectedTpas([]);
    setFilterType('client');
  }
  if (!isSpecial) {
    setFilterType('client');
  }
  if (
    selectedType === REPORT_TYPES.PA_REVIEW ||
    selectedType === REPORT_TYPES.PA_CLIENT_BILL ||
    selectedType === REPORT_TYPES.ASSIST_REVIEW ||
    selectedType === REPORT_TYPES.ASSIST_CLIENT_BILL
  ) {
    setShowWeekDropdown(true);
    setDisabledFields(true);
  } else if (
    selectedType === REPORT_TYPES.FULL_CLAIMS ||
    selectedType === REPORT_TYPES.GP_SALES_ORDER ||
    selectedType === REPORT_TYPES.INVOICE_DETAIL ||
    selectedType === REPORT_TYPES.POS_REBATE ||
    selectedType === REPORT_TYPES.MEMBER_SUMMARY ||
    selectedType === REPORT_TYPES.PA_STATUS_REPORT
  ) {
    setShowWeekDropdown(true);
    setDisabledFields(true);
    if (setAvailableWeeks) {
      setAvailableWeeks(
        [
          REPORT_TYPES.FULL_CLAIMS,
          REPORT_TYPES.GP_SALES_ORDER,
          REPORT_TYPES.INVOICE_DETAIL,
          REPORT_TYPES.MEMBER_SUMMARY,
          REPORT_TYPES.PA_STATUS_REPORT,
        ].includes(selectedType)
          ? [WEEKS.WEEK_1, WEEKS.WEEK_2, WEEKS.WEEK_3, WEEKS.WEEK_4, WEEKS.CUSTOM]
          : [WEEKS.WEEK_2, WEEKS.WEEK_4]
      );
    }
  } else {
    setShowWeekDropdown(false);
    setDisabledFields(false);
  }
};

export const handleWeekChange = (
  e: SelectChangeEvent<string>,
  setSelectedWeek: (value: string) => void,
  setDisabledFields: (value: boolean) => void,
  setMonthYearDisabled: (value: boolean) => void,
  setStartDate: (value: string) => void,
  setEndDate: (value: string) => void,
  setStartDateInvalid: (flag: boolean) => void,
  setEndDateInvalid: (flag: boolean) => void,
  setSelectedMonth: (value: string) => void,
  setSelectedYear: (value: number) => void
) => {
  const selectedValue = e.target.value;
  setSelectedWeek(selectedValue);

  setStartDateInvalid(false);
  setEndDateInvalid(false);

  if (selectedValue === WEEKS.CUSTOM) {
    setDisabledFields(false);

    setMonthYearDisabled(true);

    setStartDate('');
    setEndDate('');
    setSelectedMonth('');
    setSelectedYear?.(new Date().getFullYear());
  } else {
    setDisabledFields(true);
    setMonthYearDisabled(false);
    const { start, end } = getStartEndDate(REPORT_TYPES.FULL_CLAIMS, selectedValue, '', new Date().getFullYear());
    setStartDate('');
    setEndDate('');
  }
};

export const handleYearChange = (e: SelectChangeEvent<string>, setSelectedYear: (value: number) => void) => {
  setSelectedYear(Number(e.target.value));
};

export const getStartEndDate = (
  reportType: string,
  selectedWeek: string,
  selectedMonth: string,
  selectedYear: number
) => {
  const months = Array.from({ length: 12 }, (_, i) => new Date(2000, i, 1).toLocaleString('en-US', { month: 'long' }));
  const monthIndex = months.indexOf(selectedMonth);

  if (monthIndex === -1) return { start: '', end: '' };

  const weekRanges: Record<string, { startDay: number; endDay: number }> = {
    [WEEKS.WEEK_1]: { startDay: 1, endDay: 8 },
    [WEEKS.WEEK_2]: { startDay: 9, endDay: 15 },
    [WEEKS.WEEK_3]: { startDay: 16, endDay: 23 },
    [WEEKS.WEEK_4]: { startDay: 24, endDay: new Date(selectedYear, monthIndex + 1, 0).getDate() },
  };

  const posRebateWeeks: Record<string, { startDay: number; endDay: number }> = {
    [WEEKS.WEEK_2]: { startDay: 1, endDay: 15 },
    [WEEKS.WEEK_4]: { startDay: 16, endDay: new Date(selectedYear, monthIndex + 1, 0).getDate() },
  };

  const selectedMapping = reportType === REPORT_TYPES.POS_REBATE ? posRebateWeeks : weekRanges;

  const selectedRange = selectedMapping[selectedWeek];

  if (!selectedRange) return { start: '', end: '' };

  return {
    start: new Date(selectedYear, monthIndex, selectedRange.startDay).toLocaleDateString('en-CA'),
    end: new Date(selectedYear, monthIndex, selectedRange.endDay).toLocaleDateString('en-CA'),
  };
};

export const confirmGenerateForAll = (
  setOpenDialog: (value: boolean) => void,
  submitReport: (clients: any) => void
) => {
  setOpenDialog(false);
  submitReport([{ client_name: GENERATE_REPORTS_VALUES.ALL_CLIENTS }]);
};

const getMonthIndex = (monthName: string): number => {
  const months = Array.from({ length: 12 }, (_, i) => new Date(0, i).toLocaleString('en-US', { month: 'long' }));
  return months.indexOf(monthName);
};

export const calculateBillingDateString = (year: number, monthName: string): string => {
  if (!year || !monthName) {
    return '';
  }
  const monthIndex = getMonthIndex(monthName);
  if (monthIndex === -1) {
    return '';
  }
  const targetDate = new Date(year, monthIndex + 1, 5);
  return targetDate.toLocaleDateString('en-CA');
};

export const calculateStartEndFromBilling = (billingDateString: string) => {
  if (!billingDateString) {
    return { start: '', end: '' };
  }
  try {
    const billingDate = new Date(billingDateString + 'T00:00:00');
    const endDate = new Date(billingDate.getFullYear(), billingDate.getMonth(), 0);
    const startDate = new Date(endDate.getFullYear(), endDate.getMonth() - 5, 1);

    return {
      start: startDate.toLocaleDateString('en-CA'),
      end: endDate.toLocaleDateString('en-CA'),
    };
  } catch (error) {
    return { start: '', end: '' };
  }
};

export const calculateStartEndFromBillingAssist = (billingDateString: string) => {
  if (!billingDateString) {
    return { start: '', end: '' };
  }
  try {
    const billingDate = new Date(billingDateString + 'T00:00:00');
    const endDate = new Date(billingDate.getFullYear(), billingDate.getMonth(), 0);
    const startDate = new Date(endDate.getFullYear(), endDate.getMonth(), 1);

    return {
      start: startDate.toLocaleDateString('en-CA'),
      end: endDate.toLocaleDateString('en-CA'),
    };
  } catch {
    return { start: '', end: '' };
  }
};

// This function is kept for future flexibility in mapping report types to client data sources.
export const mapReportTypeToClientSource = (reportType: string): string => {
  if (reportType === REPORT_TYPES.ASSIST_REVIEW || reportType === REPORT_TYPES.ASSIST_CLIENT_BILL) {
    return REPORT_TYPES.PA_REVIEW;
  }

  if (reportType === REPORT_TYPES.MEMBER_SUMMARY) {
    return 'Clients';
  }

  return reportType;
};

export const isDateInFuture = (dateStr: string): boolean => {
  if (!dateStr) {
    return false;
  }

  const futureLimit = new Date();
  futureLimit.setDate(futureLimit.getDate() + 7);
  futureLimit.setHours(0, 0, 0, 0);

  let dateToCompare;
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    const parts = dateStr.split('-');
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1;
    const day = parseInt(parts[2], 10);
    dateToCompare = new Date(year, month, day);
  } else {
    dateToCompare = new Date(dateStr);
  }

  if (isNaN(dateToCompare.getTime())) {
    return true;
  }

  dateToCompare.setHours(0, 0, 0, 0);
  return dateToCompare > futureLimit;
};

export const isPaDateTooFarInFuture = (selectedYear: number, selectedMonthName: string): boolean => {
  if (!selectedYear || !selectedMonthName) {
    return false;
  }

  const selectedMonthIndex = getMonthIndex(selectedMonthName);
  if (selectedMonthIndex === -1) {
    return false;
  }

  const firstDayOfSelectedMonth = new Date(selectedYear, selectedMonthIndex, 1);
  firstDayOfSelectedMonth.setHours(0, 0, 0, 0);

  const now = new Date();
  const limitDate = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  limitDate.setHours(0, 0, 0, 0);

  return firstDayOfSelectedMonth >= limitDate;
};

export const getMasterStartEndDate = (type: string, week: string, month: string, year: number) => {
  const months = Array.from({ length: 12 }, (_, i) => new Date(2000, i, 1).toLocaleString('en-US', { month: 'long' }));

  const monthIndex = months.indexOf(month);

  const billingObj = new Date(year, monthIndex + 1, 5);
  const calculatedBillingDate = billingObj.toISOString().split('T')[0];

  if (type === REPORT_TYPES.PA_REVIEW || type === REPORT_TYPES.PA_CLIENT_BILL) {
    const endDate = new Date(year, monthIndex + 1, 0);
    const startDate = new Date(year, monthIndex - 5, 1);

    return {
      start: startDate.toISOString().split('T')[0],
      end: endDate.toISOString().split('T')[0],
    };
  }

  if (type === REPORT_TYPES.ASSIST_REVIEW || type === REPORT_TYPES.ASSIST_CLIENT_BILL) {
    const startDate = new Date(year, monthIndex, 1);
    const endDate = new Date(year, monthIndex + 1, 0);

    return {
      start: startDate.toISOString().split('T')[0],
      end: endDate.toISOString().split('T')[0],
    };
  }

  return getStartEndDate(type, week, month, year);
};
